import axios from 'axios';
import { getConfig } from '../../config/config-helper';
import { CurrentUser } from './types';

export const sendInvitations = async (
  invitations: Array<{ email: string; accessLevel: string }>,
  selectedGroupId: string,
  currentUser: CurrentUser,
  token: string,
  setIsSubmitting: (value: boolean) => void,
  setInvitedEmails: (
    value: Array<string | { email: string; access_level?: string; status?: string; gitlab_id?: string }>,
  ) => void,
  setSkippedUsers: (value: Array<{ email: string; reason?: string }>) => void,
  setShowSuccessModal: (value: boolean) => void,
  setEmails: (value: string) => void,
  setExternalDomainWarning: (value: string | null) => void,
  setShowExternalConfirmation: (value: boolean) => void,
  setPendingInvitations: (value: Array<{ email: string; accessLevel: string }>) => void,
  setError: (value: string | null) => void,
): Promise<void> => {
  setIsSubmitting(true);

  try {
    const { dataopsAdminAppApiEndpoint } = getConfig();
    const invitedBy = currentUser.emails?.nodes?.[0]?.email ?? '';

    const response = await axios.post(
      `${dataopsAdminAppApiEndpoint}/dataops_platform/invite`,
      {
        invitations,
        groupId: selectedGroupId,
        invited_by: invitedBy,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        withCredentials: false,
      },
    );

    // Process the response data
    const responseData = response.data;

    // Get skipped users first
    const skippedUserEmails: string[] = [];
    if (
      typeof responseData === 'object' &&
      responseData !== null &&
      Array.isArray(responseData.skipped_users) &&
      responseData.skipped_users.length > 0
    ) {
      responseData.skipped_users.forEach((user: { email: string; reason?: string }) => {
        skippedUserEmails.push(user.email.toLowerCase());
      });
      setSkippedUsers(responseData.skipped_users);
    } else {
      setSkippedUsers([]);
    }

    // Store invited emails for success modal, ensuring we don't include skipped users
    if (
      typeof responseData === 'object' &&
      responseData !== null &&
      typeof responseData.details === 'object' &&
      responseData.details !== null &&
      Array.isArray(responseData.details.successful_invitations) &&
      responseData.details.successful_invitations.length > 0
    ) {
      // Store the successful invitations as they are (could be objects or strings)
      setInvitedEmails(responseData.details.successful_invitations);
    } else if (responseData?.details?.successful_count > 0) {
      // If we have successful_count but no successful_invitations, filter out skipped users
      const successfulEmails = invitations
        .map((inv) => inv.email)
        .filter((email) => !skippedUserEmails.includes(email.toLowerCase()));
      setInvitedEmails(successfulEmails);
    } else {
      // If no successful invitations, set empty array
      setInvitedEmails([]);
    }

    // Show success modal instead of inline success message
    setShowSuccessModal(true);

    // Reset form
    setEmails('');
    setExternalDomainWarning(null);
    setShowExternalConfirmation(false);
    setPendingInvitations([]);
  } catch (err) {
    console.error('Failed to invite colleagues:', err);
    setError('Failed to invite colleagues. Please try again.');
  } finally {
    setIsSubmitting(false);
  }
};
